import React, { useState, useEffect } from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import CookbookWrapper from 'src/components/CookbookWrapper/CookbookWrapper';
import AuthErrorWrapper from 'src/components/AuthErrorWrapper/AuthErrorWrapper';
import { AuthProvider } from 'src/components/AuthProvider/AuthProvider';
import { SnackbarLauncher } from 'src/components/snackbar/SnackbarLauncher';
import { AnalyticsProvider } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { AnalyticsScript } from 'src/components/Analytics/AnalyticsScript';
import { OnAppInit } from 'src/components/OnAppInit/OnAppInit';
import { getWindow } from 'src/utilities/window';
import { Environment } from 'src/utilities/environment';
import { hasMalformedPathname } from 'src/utilities/routeParams';
import { EnterprisePaths } from 'src/components/AppRoutes/Enterprise/EnterprisePathsDictionary';

interface WrappedRouteProps extends RouteProps {
  shouldLoadAnalytics?: boolean;
}

const WrappedRoute = ({
  component: Component,
  ...rest
}: WrappedRouteProps): JSX.Element => {
  const [shouldLoadAnalytics, setShouldLoadAnalytics] = useState(
    process.env.GH_ENV === Environment.prod
  );
  const { pathname } = useLocation();

  useEffect(() => {
    const window = getWindow();
    const isLocalStorageDefined = !!get(window, 'localStorage');
    const isNotProdEnv = process.env.GH_ENV !== Environment.prod;

    if (isNotProdEnv && isLocalStorageDefined) {
      const isTestcafeRunning = !!window.localStorage.getItem('isTestcafe');
      setShouldLoadAnalytics(!isTestcafeRunning);
    }
  }, []);

  return (
    <Route
      {...rest}
      render={(props): React.ReactElement => {
        if (hasMalformedPathname(pathname)) {
          return <Redirect to={EnterprisePaths.notFound} />;
        }

        return (
          <AuthProvider>
            <CookbookWrapper>
              <AuthErrorWrapper>
                {shouldLoadAnalytics && <AnalyticsScript />}
                <AnalyticsProvider>
                  <OnAppInit>
                    <Component {...props} />
                    <SnackbarLauncher />
                  </OnAppInit>
                </AnalyticsProvider>
              </AuthErrorWrapper>
            </CookbookWrapper>
          </AuthProvider>
        );
      }}
    />
  );
};

export default WrappedRoute;
